import { Injectable, InjectionToken, inject, signal, computed, type ResourceRef } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { map, of, retry, throwError, timer } from 'rxjs';
import type { UserDetailsResponse, UserPermissionsResponse } from '@zag/auth/models';
import { FeatureAccessClientService } from '@zag/features/ui';

/**
 * Retry configuration for the /api/v1/users/me call.
 * Overridable in tests to disable async retries that would otherwise leak
 * `timer()` subscriptions across `TestBed` recreations.
 */
export const USERS_ME_RETRY_COUNT = new InjectionToken<number>('USERS_ME_RETRY_COUNT', {
  providedIn: 'root',
  factory: () => 3,
});

export const USERS_ME_RETRY_BASE_DELAY_MS = new InjectionToken<number>('USERS_ME_RETRY_BASE_DELAY_MS', {
  providedIn: 'root',
  factory: () => 300,
});

/**
 * Service for managing user permissions and roles in the frontend.
 * Uses Angular's rxResource for reactive permission loading with automatic state management.
 *
 * Access control is handled via route-based redirects (not layout-based state).
 * Guards check permissions and redirect to /auth/pending, /auth/denied, or /auth/error.
 *
 * @example
 * ```typescript
 * // In a component
 * private readonly permissionService = inject(PermissionService);
 *
 * // Check a specific permission
 * if (this.permissionService.hasPermission('plans:read')) {
 *   // User can read plans
 * }
 *
 * // Use computed signal for reactive UI
 * readonly canEdit = this.permissionService.canAccessPlanning;
 *
 * // Wait for permissions to load in guards
 * await firstValueFrom(toObservable(permissionService.isLoaded).pipe(filter(loaded => loaded)));
 * ```
 */
@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly http = inject(HttpClient);
  private readonly retryCount = inject(USERS_ME_RETRY_COUNT);
  private readonly retryBaseDelayMs = inject(USERS_ME_RETRY_BASE_DELAY_MS);
  private readonly featureAccess = inject(FeatureAccessClientService);

  // Trigger signal for manual reloads
  private readonly _loadTrigger = signal(0);

  /**
   * Resource that loads permissions on demand.
   * Uses rxResource for automatic state management.
   *
   * Call loadPermissions() to trigger a fetch.
   */
  readonly permissionsResource: ResourceRef<UserPermissionsResponse> = rxResource({
    params: () => ({ trigger: this._loadTrigger() }),
    stream: ({ params }) => {
      // If trigger is 0, don't load (initial state)
      if (params.trigger === 0) {
        return of({ data: { roles: [], permissions: [] } });
      }

      return this.http.get<UserDetailsResponse>('/api/v1/users/me').pipe(
        retry({
          count: this.retryCount,
          // Exponential backoff on transient failures (network / 5xx). Real
          // auth failures (401/403) or client errors (4xx) are surfaced
          // immediately so guards route to the correct auth page instead of
          // masking them behind retry loops.
          delay: (error, retryIndex) => {
            const status = error instanceof HttpErrorResponse ? error.status : 0;
            const isTransient = status === 0 || status >= 500;
            if (!isTransient) {
              return throwError(() => error);
            }
            return timer(this.retryBaseDelayMs * 2 ** (retryIndex - 1));
          },
        }),
        map(response => {
          const directRoleIds = response.directRoles.map(role => role.id);
          const inheritedRoleIds = response.inheritedRoles.map(role => role.id);
          const permissions = response.effectivePermissions.map(permission => permission.id);

          return {
            data: {
              roles: Array.from(new Set([...directRoleIds, ...inheritedRoleIds])),
              permissions: Array.from(new Set(permissions)),
            },
          };
        })
      );
    },
    defaultValue: { data: { roles: [], permissions: [] } },
  });

  // Why: resource.value() throws in an error state, which would reach ErrorHandler.
  readonly permissions = computed(() =>
    this.permissionsResource.status() === 'error' ? [] : (this.permissionsResource.value()?.data.permissions ?? [])
  );

  readonly roles = computed(() =>
    this.permissionsResource.status() === 'error' ? [] : (this.permissionsResource.value()?.data.roles ?? [])
  );

  readonly isLoading = computed(() => this.permissionsResource.isLoading());

  readonly isLoaded = computed(() => {
    const status = this.permissionsResource.status();
    return status === 'resolved' || status === 'error' || status === 'local';
  });

  readonly hasError = computed(() => this.permissionsResource.status() === 'error');

  readonly error = computed(() => this.permissionsResource.error());

  // Computed signals for common permission checks
  readonly hasNoPermissions = computed(() => this.permissions().length === 0 && this.roles().length === 0);

  /**
   * Computed signal: Can access planning-related features
   * Requires any of: plans:read, plans:create, plans:update, plans:delete
   */
  readonly canAccessPlanning = computed(() => this.hasAnyPermission(['plans:read', 'plans:create', 'plans:update', 'plans:delete']));

  /**
   * Computed signal: Can access employee-related features
   * Requires any of: employees:read, employees:create, employees:update, employees:delete
   */
  readonly canAccessEmployees = computed(() =>
    this.hasAnyPermission(['employees:read', 'employees:create', 'employees:update', 'employees:delete'])
  );

  /**
   * Computed signal: Can access shift-related features
   * Requires any of: shifts:read, shifts:create, shifts:update, shifts:delete
   */
  readonly canAccessShifts = computed(() => this.hasAnyPermission(['shifts:read', 'shifts:create', 'shifts:update', 'shifts:delete']));

  /**
   * Computed signal: Can access team-related features
   * Requires any of: teams:read, teams:create, teams:update, teams:delete
   */
  readonly canAccessTeams = computed(() => this.hasAnyPermission(['teams:read', 'teams:create', 'teams:update', 'teams:delete']));

  /**
   * Computed signal: Can access skills-related features
   * Requires any of: skills:read, skills:create, skills:update, skills:delete
   */
  readonly canAccessSkills = computed(() => this.hasAnyPermission(['skills:read', 'skills:create', 'skills:update', 'skills:delete']));

  /**
   * Computed signal: Can access settings page
   * Requires settings:update permission (admin only)
   */
  readonly canAccessSettings = computed(() => this.hasPermission('settings:update'));

  /**
   * Computed signal: Can access dashboard
   * Requires dashboard:read permission (all roles by default)
   */
  readonly canAccessDashboard = computed(() => this.hasPermission('dashboard:read'));

  /**
   * Computed signal: User is an admin
   */
  readonly isAdmin = computed(() => this.hasRole('admin'));

  // ==========================================================================
  // Granular Employee Section Permissions (Story 3.10)
  // ==========================================================================

  /**
   * Computed signal: Can read employee address information
   * Required to view the Address section on person detail page
   */
  readonly canReadEmployeeAddress = computed(() => this.hasPermission('employees:address:read'));

  /**
   * Computed signal: Can update employee address information
   * Required to edit the Address section on person detail page
   */
  readonly canUpdateEmployeeAddress = computed(() => this.hasPermission('employees:address:update'));

  /**
   * Computed signal: Can read employee contacts
   * Required to view the Contacts section on person detail page
   */
  readonly canReadEmployeeContacts = computed(() => this.hasPermission('employees:contacts:read'));

  /**
   * Computed signal: Can update employee contacts
   * Required to edit the Contacts section on person detail page
   */
  readonly canUpdateEmployeeContacts = computed(() => this.hasPermission('employees:contacts:update'));

  /**
   * Computed signal: Can read employee team assignments
   * Required to view the Team Assignments section on person detail page
   */
  readonly canReadEmployeeTeams = computed(() => this.hasPermission('employees:teams:read'));

  /**
   * Computed signal: Can update employee team assignments
   * Required to edit the Team Assignments section on person detail page
   */
  readonly canUpdateEmployeeTeams = computed(() => this.hasPermission('employees:teams:update'));

  /**
   * Computed signal: Can read employee skills
   * Required to view the Skills section on person detail page
   */
  readonly canReadEmployeeSkills = computed(() => this.hasPermission('employees:skills:read'));

  /**
   * Computed signal: Can update employee skills
   * Required to edit the Skills section on person detail page
   */
  readonly canUpdateEmployeeSkills = computed(() => this.hasPermission('employees:skills:update'));

  /**
   * Computed signal: Can read employee time balances
   */
  readonly canReadEmployeeBalances = computed(() => this.hasPermission('employees:balances:read'));

  /**
   * Computed signal: Can update employee time balances
   */
  readonly canUpdateEmployeeBalances = computed(() => this.hasPermission('employees:balances:update'));

  /**
   * Computed signal: Can read absences
   */
  readonly canReadAbsences = computed(() => this.hasPermission('timetracking:absences:read'));

  /**
   * Computed signal: Can create/edit/delete absences
   */
  readonly canUpdateAbsences = computed(() => this.hasPermission('timetracking:absences:update'));

  /**
   * Check if user has a specific permission
   * @param permission - Permission string in format "resource:action" (e.g., "plans:read")
   * @returns true if user has the permission
   */
  hasPermission(permission: string): boolean {
    const perms = this.permissions();

    let hasRaw = false;
    if (perms.includes(permission)) {
      hasRaw = true;
    } else {
      const [resource] = permission.split(':');
      if (perms.includes(`${resource}:*`) || perms.includes('*:*') || perms.includes('*')) {
        hasRaw = true;
      }
    }
    if (!hasRaw) return false;

    // Feature gate: the permission is only effective if its owning feature is
    // active (or the permission has feature_key = NULL — foundation). The
    // FeatureAccessClientService.hasPermission consults the server-computed
    // `effectivePermissions` from GET /api/v1/features which already applies
    // this intersection. Fail-open while unresolved (matches existing policy);
    // the server-side FeatureGuard is the real enforcement.
    return this.featureAccess.hasPermission(permission);
  }

  /**
   * Check if user has any of the specified permissions
   * @param permissions - Array of permission strings
   * @returns true if user has at least one of the permissions
   */
  hasAnyPermission(permissions: string[]): boolean {
    return permissions.some(permission => this.hasPermission(permission));
  }

  /**
   * Check if user has all of the specified permissions
   * @param permissions - Array of permission strings
   * @returns true if user has all of the permissions
   */
  hasAllPermissions(permissions: string[]): boolean {
    return permissions.every(permission => this.hasPermission(permission));
  }

  /**
   * Check if user has a specific role
   * @param role - Role name (e.g., "admin", "planner")
   * @returns true if user has the role
   */
  hasRole(role: string): boolean {
    return this.roles().includes(role);
  }

  /**
   * Check if user has any of the specified roles
   * @param roles - Array of role names
   * @returns true if user has at least one of the roles
   */
  hasAnyRole(roles: string[]): boolean {
    const userRoles = this.roles();
    return roles.some(role => userRoles.includes(role));
  }

  /**
   * Check if user has all of the specified roles
   * @param roles - Array of role names
   * @returns true if user has all of the roles
   */
  hasAllRoles(roles: string[]): boolean {
    const userRoles = this.roles();
    return roles.every(role => userRoles.includes(role));
  }

  /**
   * Load permissions from the backend.
   * Triggers the rxResource to fetch permissions.
   * Called by AuthService after login/session restore.
   */
  loadPermissions(): void {
    // Increment trigger to initiate a load
    this._loadTrigger.update(v => v + 1);
    this.featureAccess.refresh();
  }

  /**
   * Clear all permissions and roles.
   * Called automatically on logout.
   */
  clearPermissions(): void {
    // Reset trigger to idle state
    this._loadTrigger.set(0);
  }

  // ==========================================================================
  // Hierarchical Permission Methods (for field-level RBAC)
  // ==========================================================================

  /**
   * Check permission with hierarchical fallback.
   * Checks in order: field-level → section-level → entity-level
   *
   * This enables granular permission control while maintaining backwards compatibility.
   * Users with base permissions (e.g., 'teams:update') automatically have access to
   * all fields unless a more specific permission explicitly restricts it.
   *
   * @example
   * // Check field-level permission with fallback
   * hasHierarchicalPermission('teams:details:name:update')
   * // Checks in order:
   * // 1. teams:details:name:update (field-level)
   * // 2. teams:details:update (section-level)
   * // 3. teams:update (entity-level)
   * // Returns true if ANY of these permissions exist
   *
   * @param fullPermission - Most specific permission string (e.g., 'teams:details:name:update')
   * @returns true if user has the permission at any level of the hierarchy
   */
  hasHierarchicalPermission(fullPermission: string): boolean {
    // Check exact permission first (most specific)
    if (this.hasPermission(fullPermission)) {
      return true;
    }

    // Build hierarchy by removing segments from the middle
    const parts = fullPermission.split(':');

    // Hierarchy examples:
    // 'teams:details:name:update' → 'teams:details:update' → 'teams:update'
    // 'shifts:timing:startTime:read' → 'shifts:timing:read' → 'shifts:read'

    // Try less specific permissions by removing segments from the middle
    while (parts.length > 2) {
      // Remove the second-to-last segment (the field or section name)
      parts.splice(-2, 1);
      const fallbackPermission = parts.join(':');

      if (this.hasPermission(fallbackPermission)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if user can read a specific field with hierarchical fallback.
   *
   * @example
   * // Check if user can read team name
   * canReadField('teams', 'details', 'name')
   * // Checks: teams:details:name:read → teams:details:read → teams:read
   *
   * // Check if user can read team details section
   * canReadField('teams', 'details')
   * // Checks: teams:details:read → teams:read
   *
   * @param resource - Resource name (e.g., 'teams', 'skills', 'shifts')
   * @param section - Optional section name (e.g., 'details', 'appearance', 'timing')
   * @param field - Optional field name (e.g., 'name', 'description', 'startTime')
   * @returns true if user has read permission at any level
   */
  canReadField(resource: string, section?: string, field?: string): boolean {
    const permissionParts = [resource];
    if (section) permissionParts.push(section);
    if (field) permissionParts.push(field);
    permissionParts.push('read');

    return this.hasHierarchicalPermission(permissionParts.join(':'));
  }

  /**
   * Check if user can update a specific field with hierarchical fallback.
   *
   * @example
   * // Check if user can update team name
   * canUpdateField('teams', 'details', 'name')
   * // Checks: teams:details:name:update → teams:details:update → teams:update
   *
   * // Check if user can update team details section
   * canUpdateField('teams', 'details')
   * // Checks: teams:details:update → teams:update
   *
   * @param resource - Resource name (e.g., 'teams', 'skills', 'shifts')
   * @param section - Optional section name (e.g., 'details', 'appearance', 'timing')
   * @param field - Optional field name (e.g., 'name', 'description', 'startTime')
   * @returns true if user has update permission at any level
   */
  canUpdateField(resource: string, section?: string, field?: string): boolean {
    const permissionParts = [resource];
    if (section) permissionParts.push(section);
    if (field) permissionParts.push(field);
    permissionParts.push('update');

    return this.hasHierarchicalPermission(permissionParts.join(':'));
  }
}
